import {
  Box,
  Grid2 as Grid,
  Typography,
  Chip,
  Alert,
  Button as MuiButton,
  Autocomplete,
  TextField,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Tab, useRecordContext, useGetOne, useGetList, useUpdate, useRefresh } from "react-admin";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { getResourceIcon } from "@/utils/icons";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { ClaimItemMetrics } from "./components/ClaimItemMetrics";
import { ClaimItemOverview } from "./components/ClaimItemOverview";
import { ClaimItemFinancialImpact } from "./components/ClaimItemFinancialImpact";
import { ClaimItemProcessingStatus } from "./components/ClaimItemProcessingStatus";
import { ClaimItemServiceIdentification } from "./components/ClaimItemServiceIdentification";
import { EligibilityBanner } from "./components/EligibilityBanner";
import { ClaimItem, Claimant, Client, ResourceName, StoplossClaim } from "@/types";
import { PageHeaderActions } from "@/components/common/PageHeaderActions";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { ColorType } from "@/types/shared/uiComponents";
import { Link as RouterLink } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import { LinkOff as LinkOffIcon, ArrowRightAlt as ArrowIcon } from "@mui/icons-material";
import { CodeDescriptionLine } from "@/components/common/fields/CodeDescriptionLine";

// ─── Helper ───────────────────────────────────────────────────────────────

const isValidRef = (ref?: string | null): boolean =>
  !!ref && ref !== "" && ref !== "00000000-0000-0000-0000-000000000000";

// ─── Main ─────────────────────────────────────────────────────────────────

export const ClaimItemShow = () => {
  return (
    <StyledShow actions={false}>
      <ClaimItemShowContent />
    </StyledShow>
  );
};

const ClaimItemShowContent = () => {
  const record = useRecordContext<ClaimItem>();
  const ClaimItemIcon = getResourceIcon(ResourceName.CLAIM_ITEMS);
  const { canDelete, canEdit } = useResourcePermissions(ResourceName.CLAIM_ITEMS);
  const [reassignOpen, setReassignOpen] = useState(false);

  const { data: claimant } = useGetOne<Claimant>(
    ResourceName.CLAIMANTS,
    { id: record?.claimant_id_ref || "" },
    { enabled: !!record?.claimant_id_ref }
  );
  const { data: client } = useGetOne<Client>(
    ResourceName.CLIENTS,
    { id: record?.client_id_ref || "" },
    { enabled: !!record?.client_id_ref }
  );

  // Title: provider/type name (truncates naturally via PageHeader maxWidth)
  // Amount goes in customTitleTag so it never gets truncated
  const title = record?.provider || formatToTitleCase(record?.claim_type || "Claim Item");
  const amountTag = record?.paid_amount ? formatUSD(record.paid_amount) : undefined;

  const subtitle = [claimant?.claimant_name, client?.client_legal_name].filter(Boolean).join(" · ");

  return (
    <Box>
      <PageHeader
        customTitle={title}
        customTitleTag={amountTag}
        statusKey="claim_status"
        customSubtitle={subtitle}
        headerIcon={<ClaimItemIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        rightContent={
          <PageHeaderActions
            actions={{
              delete: canDelete,
            }}
          >
            {canEdit && (
              <Tooltip title="Reassign claim item">
                <IconButton size="small" onClick={() => setReassignOpen(true)} sx={{ color: "primary.main" }}>
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </PageHeaderActions>
        }
      />
      <PageContent>
        <ClaimItemMetrics />
        <Box mt={2}>
          <StyledTabbedShowLayout>
            <Tab label="Overview" path="">
              <ClaimItemOverviewTab />
            </Tab>
            <Tab label="Service & Diagnosis" path="service">
              <TabContent>
                <ClaimItemServiceIdentification />
              </TabContent>
            </Tab>
            <Tab label="Provider" path="provider">
              <ClaimItemProviderTab />
            </Tab>
            <Tab label="Audit Log" path="audit">
              <AuditLogBox tableName={ResourceName.CLAIM_ITEMS} />
            </Tab>
          </StyledTabbedShowLayout>
        </Box>
      </PageContent>

      {/* Reassign Dialog */}
      {record && <ReassignClaimItemDialog open={reassignOpen} onClose={() => setReassignOpen(false)} record={record} />}
    </Box>
  );
};

// ─── Reassign Dialog ──────────────────────────────────────────────────────

const ReassignClaimItemDialog = ({
  open,
  onClose,
  record,
}: {
  open: boolean;
  onClose: () => void;
  record: ClaimItem;
}) => {
  const [update] = useUpdate();
  const refresh = useRefresh();
  const [selectedClient, setSelectedClient] = useState<string>(record.client_id_ref || "");
  const [selectedClaimant, setSelectedClaimant] = useState<string>(record.claimant_id_ref || "");
  const [selectedPolicy, setSelectedPolicy] = useState<string>(record.policy_id_ref || "");

  const { data: clients = [] } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });

  const { data: claimants = [] } = useGetList<Claimant>(ResourceName.CLAIMANTS, {
    filter: selectedClient ? { client_id_ref: selectedClient } : {},
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "claimant_name", order: "ASC" },
  });

  const { data: policies = [] } = useGetList(ResourceName.POLICIES, {
    filter: selectedClient ? { client_id_ref: selectedClient } : {},
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "effective_start_date", order: "DESC" },
  });

  // Auto-select active policy when claimant changes
  const handleClaimantChange = (_: unknown, val: Claimant | null) => {
    setSelectedClaimant(val?.id || "");
    // Auto-assign the active policy for this client
    if (val && policies.length > 0) {
      const activePolicy = policies.find((p) => p.status === "active");
      if (activePolicy) {
        setSelectedPolicy(activePolicy.id);
      }
    } else {
      setSelectedPolicy("");
    }
  };

  const handleSave = async () => {
    await update(ResourceName.CLAIM_ITEMS, {
      id: record.id,
      data: {
        client_id_ref: selectedClient,
        claimant_id_ref: selectedClaimant,
        policy_id_ref: selectedPolicy,
      },
      previousData: record,
    });
    onClose();
    refresh();
  };

  // Reset selections when dialog opens
  const handleOpen = () => {
    setSelectedClient(record.client_id_ref || "");
    setSelectedClaimant(record.claimant_id_ref || "");
    setSelectedPolicy(record.policy_id_ref || "");
  };

  // Find auto-selected policy label for hint
  const autoSelectedPolicy = policies.find((p) => p.id === selectedPolicy);
  const wasAutoSelected =
    selectedPolicy && selectedPolicy !== record.policy_id_ref && autoSelectedPolicy?.status === "active";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleOpen }}>
      <DialogTitle>Reassign Claim Item</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={clients}
            getOptionLabel={(opt) => opt.client_legal_name || ""}
            value={clients.find((c) => c.id === selectedClient) || null}
            onChange={(_, val) => {
              setSelectedClient(val?.id || "");
              setSelectedClaimant("");
              setSelectedPolicy("");
            }}
            size="small"
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
          <Autocomplete
            options={claimants}
            getOptionLabel={(opt) => opt.claimant_name || ""}
            value={claimants.find((c) => c.id === selectedClaimant) || null}
            onChange={handleClaimantChange}
            size="small"
            disabled={!selectedClient}
            renderInput={(params) => <TextField {...params} label="Claimant" />}
          />
          <Box>
            <Autocomplete
              options={policies}
              getOptionLabel={(opt) => {
                const name = opt.vendor_company_name || "No Carrier";
                const year = opt.effective_start_date?.slice(0, 4) || "";
                return `${name} ${year}`;
              }}
              renderOption={(props, opt) => {
                const name = opt.vendor_company_name || "No Carrier";
                const year = opt.effective_start_date?.slice(0, 4) || "";
                return (
                  <li {...props} key={opt.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                      <Typography variant="body2">
                        {name} {year}
                      </Typography>
                      {opt.status === "active" && (
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      )}
                    </Box>
                  </li>
                );
              }}
              value={policies.find((p) => p.id === selectedPolicy) || null}
              onChange={(_, val) => setSelectedPolicy(val?.id || "")}
              size="small"
              disabled={!selectedClient}
              renderInput={(params) => <TextField {...params} label="Policy" />}
            />
            {wasAutoSelected && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                Auto-assigned to active policy
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton
          variant="contained"
          onClick={handleSave}
          disabled={!selectedClient || !selectedClaimant}
          disableElevation
        >
          Reassign
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// ─── Overview Tab ─────────────────────────────────────────────────────────

const ClaimItemOverviewTab = () => {
  const record = useRecordContext<ClaimItem>();
  const { canEdit } = useResourcePermissions(ResourceName.CLAIM_ITEMS);
  const isEligible = record?.is_eligible !== false;

  return (
    <TabContent>
      {/* Ineligible banner with mark-as-eligible action */}
      {!isEligible && <EligibilityBanner record={record} canEdit={canEdit} />}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: isEligible ? 7 : 12 }}>
          <ClaimItemOverview />
          <Box mt={2}>
            <ClaimItemFinancialImpact />
          </Box>
          {/* #7348: paid_date / check_number editors — section existed but was
              never mounted after the #3632 list/show rework. */}
          <Box mt={2}>
            <ClaimItemProcessingStatus />
          </Box>
        </Grid>

        {/* Linked Claim — only for eligible items */}
        {isEligible && (
          <Grid size={{ xs: 12, lg: 5 }}>
            <ClaimItemLinkedClaim />
          </Grid>
        )}
      </Grid>
    </TabContent>
  );
};

// ─── Linked Claim Card (matches reimbursement LinkedClaimCard pattern) ────

const safeUSD = (n: unknown) => (typeof n === "number" && !Number.isNaN(n) ? formatUSD(n) : "—");

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
  </Box>
);

const ClaimItemLinkedClaim = () => {
  const record = useRecordContext<ClaimItem>();
  const { canEdit } = useResourcePermissions(ResourceName.CLAIM_ITEMS);
  const [isLinking, setIsLinking] = useState(false);
  const refresh = useRefresh();

  const claimId = record?.stoploss_claim_id_ref;
  const isLinked = isValidRef(claimId);

  const { data: claim, isLoading } = useGetOne<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    { id: claimId || "" },
    { enabled: !!isLinked }
  );

  // Fetch available claims for linking (scoped to claimant + client + policy)
  const claimFilter: Record<string, string> = {};
  if (record?.claimant_id_ref) claimFilter.claimant_id_ref = record.claimant_id_ref;
  if (record?.client_id_ref) claimFilter.client_id_ref = record.client_id_ref;
  if (record?.policy_id_ref) claimFilter.policy_id_ref = record.policy_id_ref;

  const { data: availableClaims = [], isLoading: isLoadingClaims } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      filter: claimFilter,
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_datetime", order: "DESC" },
    },
    { enabled: isLinking && !!record?.claimant_id_ref }
  );

  // Use PATCH via httpClient to avoid PUT sending full record through orchestrator
  const patchClaimItem = async (data: Record<string, unknown>) => {
    const apiUrl = getPartnerAwareApiUrl();
    await httpClient(`${apiUrl}/claim-items/${record?.id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    refresh();
  };

  const handleLink = async (claimToLink: StoplossClaim | null) => {
    if (!claimToLink || !record) return;
    await patchClaimItem({ stoploss_claim_id_ref: claimToLink.id });
    setIsLinking(false);
  };

  const handleUnlink = async () => {
    if (!record) return;
    await patchClaimItem({ stoploss_claim_id_ref: "" });
  };

  // Not linked — show warning with link action
  if (!isLinked) {
    return (
      <ShowAccordionSection label="Linked Stop-Loss Claim" defaultExpanded>
        <Alert
          severity="warning"
          icon={<LinkOffIcon fontSize="small" />}
          sx={{ mb: isLinking ? 2 : 0, fontSize: "0.8125rem" }}
          action={
            canEdit && !isLinking ? (
              <MuiButton size="small" onClick={() => setIsLinking(true)} sx={{ whiteSpace: "nowrap" }}>
                Link Claim
              </MuiButton>
            ) : undefined
          }
        >
          Not linked to a stop-loss claim.
        </Alert>

        {isLinking && (
          <Autocomplete
            options={availableClaims}
            loading={isLoadingClaims}
            getOptionLabel={(option) =>
              `${formatToTitleCase(option.stoploss_claim_type || "Claim")} · ${safeUSD(option.total_paid_amount)} · ${formatToTitleCase(option.claim_status)}`
            }
            onChange={(_, value) => handleLink(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a claim to link"
                size="small"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingClaims ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            size="small"
            noOptionsText={
              record?.claimant_id_ref ? "No claims found for this claimant" : "Set a claimant first to find claims"
            }
          />
        )}
      </ShowAccordionSection>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <ShowAccordionSection label="Linked Stop-Loss Claim" defaultExpanded>
        <Typography variant="body2" color="text.secondary">
          Loading claim...
        </Typography>
      </ShowAccordionSection>
    );
  }

  // Not found
  if (!claim) {
    return (
      <ShowAccordionSection label="Linked Stop-Loss Claim" defaultExpanded>
        <Alert severity="info" icon={<InfoIcon fontSize="small" />} sx={{ fontSize: "0.8125rem" }}>
          Linked claim not found.
        </Alert>
      </ShowAccordionSection>
    );
  }

  // Show claim summary — matching reimbursement pattern
  return (
    <ShowAccordionSection label="Linked Stop-Loss Claim" defaultExpanded>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2" fontWeight={600}>
          {formatToTitleCase(claim.stoploss_claim_type || "Claim")}
        </Typography>
        <StatusChip
          label={formatToTitleCase(claim.claim_status)}
          color={getStatusColor(claim.claim_status) ?? ColorType.Default}
        />
      </Stack>

      {/* Stats highlight */}
      <Box sx={{ bgcolor: "action.hover", borderRadius: 1, px: 2, py: 1.5, mb: 2 }}>
        <Typography variant="body1" fontWeight={600} color="primary.main">
          {safeUSD(claim.total_paid_amount)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Total Paid
          </Typography>
          {(claim as unknown as Record<string, unknown>).claim_items_count !== undefined && (
            <Typography variant="caption" color="text.disabled">
              · {String((claim as unknown as Record<string, unknown>).claim_items_count)} items
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Date stats */}
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={3}>
          <Stat label="First Service" value={formatLongDate(claim.first_service_date)} />
          <Stat label="Last Service" value={formatLongDate(claim.last_service_date)} />
        </Stack>
        <Stack direction="row" spacing={3}>
          <Stat label="First Paid" value={formatLongDate(claim.first_paid_date)} />
          <Stat label="Last Paid" value={formatLongDate(claim.last_paid_date)} />
        </Stack>
        {claim.submitted_date && <Stat label="Submitted" value={formatLongDate(claim.submitted_date)} />}
      </Stack>

      {/* Actions */}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <MuiButton
          component={RouterLink}
          to={`/stoploss-claims/${claim.id}/show`}
          variant="outlined"
          size="small"
          endIcon={<ArrowIcon sx={{ fontSize: 16 }} />}
          fullWidth
        >
          View Claim Details
        </MuiButton>
      </Stack>
      {canEdit && (
        <MuiButton size="small" color="error" onClick={handleUnlink} sx={{ fontSize: "0.7rem", mt: 1 }}>
          Unlink
        </MuiButton>
      )}
    </ShowAccordionSection>
  );
};

// ─── Provider Tab ─────────────────────────────────────────────────────────

const ClaimItemProviderTab = () => {
  const record = useRecordContext<ClaimItem>();

  const fields = [
    { label: "Provider", value: record?.provider },
    { label: "Provider TIN", value: record?.provider_tin },
    { label: "Provider Type", value: record?.provider_type },
    { label: "Provider Address", value: record?.provider_address },
    { label: "Provider City", value: record?.provider_city },
    { label: "Provider State", value: record?.provider_state },
    { label: "Provider Zip", value: record?.provider_zip_code },
  ];

  const rxFields = [
    { label: "Drug Name", value: record?.drug_name },
    { label: "Drug NDC", value: record?.drug_ndc },
    { label: "Pharmacy Name", value: record?.pharmacy_name },
    { label: "Pharmacy NPI", value: record?.pharmacy_npi },
  ];

  const hasRxData = rxFields.some((f) => f.value && f.value !== "NA");

  return (
    <TabContent>
      <ShowAccordionSection label="Provider Information" defaultExpanded>
        <Grid container spacing={2}>
          {fields.map(
            (field) =>
              field.value && (
                <Grid key={field.label} size={{ xs: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    {field.label}
                  </Typography>
                  <Typography variant="body2">{field.value}</Typography>
                </Grid>
              )
          )}
        </Grid>
      </ShowAccordionSection>

      {hasRxData && (
        <Box mt={2}>
          <ShowAccordionSection label="Pharmacy / Drug Information" defaultExpanded>
            <Grid container spacing={2}>
              {rxFields.map(
                (field) =>
                  field.value &&
                  field.value !== "NA" && (
                    <Grid key={field.label} size={{ xs: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary">
                        {field.label}
                      </Typography>
                      <Typography variant="body2">{field.value}</Typography>
                    </Grid>
                  )
              )}
            </Grid>
            {/* Non-blocking NDC enrichment — hidden when drug_ndc is absent or not in the ref table. */}
            <Box sx={{ mt: 1 }}>
              <CodeDescriptionLine label="Drug" codeType="ndc" code={record?.drug_ndc} />
            </Box>
          </ShowAccordionSection>
        </Box>
      )}
    </TabContent>
  );
};
